import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Siren, Bell, Loader2 } from "lucide-react";
import { useApp } from "../../context/AppContext";

const errMsg = (e) => e?.response?.data?.detail || e.message;

/* 设计费付款催收升级（R13，原设计部自动化中心面板 → 财务中心 收入台账）：加载即只读预览；财务 / CEO / admin 可立即扫描并升级 CEO */
export const DesignFeeEscalationPanel = ({ zh }) => {
  const { api, user } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const canRun = ["admin", "finance", "ceo"].includes(user?.role);
  const run = useCallback(async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post("/design/v3/automation/payment-escalation", { dryRun });
      setRes(data);
      if (!dryRun) toast.success(zh ? `已升级：通知 ${data.notified} 人（${data.items.filter((x) => x.due).length} 笔到期升级）` : `Escalated · notified ${data.notified}`);
    } catch (e) { if (!dryRun) toast.error(errMsg(e)); }
    setBusy(false);
  }, [api, zh]);
  useEffect(() => { if (canRun) run(true); }, [canRun, run]);
  if (!canRun) return null;
  const days = res?.days || 30;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid="fin-design-escalation">
      <div className="flex flex-wrap items-center gap-2">
        <Siren className="h-4 w-4 text-red-600" />
        <h3 className="text-sm font-semibold text-slate-800" data-testid="fin-design-escalation-title">{zh ? `设计费付款催收升级（R13 · 到期超 ${days} 天 → CEO）` : `Design fee escalation (R13 · ${days}+ days overdue → CEO)`}</h3>
        {res && <span className={`text-xs px-2 py-0.5 rounded-full ${res.count ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="fin-design-escalation-count">{zh ? `${res.count} 笔超期` : `${res.count} overdue`}{!res.enabled && <span className="ml-1 text-slate-500">{zh ? "· 规则已关闭" : "· rule off"}</span>}</span>}
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" disabled={busy} onClick={() => run(true)} className="h-7 text-xs" data-testid="fin-design-escalation-preview">{busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}{zh ? "刷新预览" : "Preview"}</Button>
          <Button size="sm" disabled={busy || !res?.count} onClick={() => { if (window.confirm(zh ? "立即向 CEO（抄送财务与设计经理）发送铃铛升级？" : "Escalate to CEO now?")) run(false); }} className="h-7 text-xs bg-slate-900 hover:bg-slate-700 text-white" data-testid="fin-design-escalation-run"><Bell className="h-3 w-3 mr-1" />{zh ? "立即扫描并升级" : "Escalate now"}</Button>
        </span>
      </div>
      <p className="text-xs text-slate-500">{zh ? `每日 08:50 自动运行：设计费里程碑「到期 / 已开票」超过 ${days} 天仍未收款 → 铃铛升级 CEO（抄送财务与设计经理），每 ${days} 天升级一次直到收款。天数在 设计部 → 自动化中心 R13 规则里调整。` : `Runs daily 08:50: design-fee milestones due / invoiced ${days}+ days unpaid → bell to CEO (cc finance & design manager), repeats every ${days} days.`}</p>
      {res && res.items.length > 0 && (
        <div className="space-y-1" data-testid="fin-design-escalation-list">
          {res.items.map((x) => (
            <p key={x.id} className="text-[11px] text-slate-600 flex flex-wrap items-center gap-2" data-testid={`fin-design-escalation-item-${x.id}`}>
              <button type="button" onClick={() => nav(`/design/projects/${x.pid}`)} className="text-violet-700 hover:underline">{x.address}</button>
              <span className="text-slate-800 font-semibold">{x.milestone} ${Number(x.amount || 0).toLocaleString()}</span>
              <span className="text-red-700 font-bold">{zh ? `到期 ${x.overdueDays} 天未收` : `${x.overdueDays}d overdue`}</span>
              <span className="text-slate-400">{x.status === "Invoiced" ? (zh ? "已开票" : "Invoiced") : (zh ? "未开票" : "Not invoiced")} · {zh ? "顾问" : "Sales"} {x.sales || "—"}</span>
              {x.count > 0 && <span className="text-[10px] px-1.5 rounded bg-red-100 text-red-700">{zh ? `已升级 ${x.count} 次` : `escalated ×${x.count}`}</span>}
              {!x.due && <span className="text-[10px] px-1.5 rounded bg-slate-100 text-slate-500">{zh ? "未到下次升级" : "not due yet"}</span>}
            </p>
          ))}
          <p className="text-[10px] text-slate-400">{zh ? `CEO：${(res.ceo || []).join("、") || "未配置"}` : `CEO: ${(res.ceo || []).join(", ")}`}</p>
        </div>
      )}
      {res && !res.items.length && <p className="text-xs text-emerald-600" data-testid="fin-design-escalation-empty">{zh ? "当前没有超期未收的设计费里程碑" : "No overdue design-fee milestones"}</p>}
    </div>
  );
};
